import React, { useCallback, useEffect, useState } from "react";

import { Form, ModalDialog, StringField, SubmitButton } from "@culturehq/components";

import { StoryQuestion } from "chq";
import { useDoor } from "app/hooks";
import { t, Trans } from "app/utils";

import { MagicWandIcon } from "components/common/icons";
import ChqModalDialog from "components/common/ChqModalDialog";
import AIStoryQuestionModalForm from "./AIStoryQuestionModalForm";
import styles from "./newAIStoryQuestionModal.module.scss";

interface ModalProps {
  children: any;
  storyQuestion?: StoryQuestion;
  onCreate?: (storyQuestion: StoryQuestion) => void;
  onUpdate?: (storyQuestion: StoryQuestion) => void;
}

const NewAIStoryQuestionModal: React.FC<ModalProps> = ({
  children,
  storyQuestion,
  onCreate,
  onUpdate
}) => {
  const [changed, setChanged] = useState(false);
  const { open, onOpen: onOpenPromptGoalModal, onClose: onClosePromptGoalModal } = useDoor(false);
  const [promptDescription, setPromptDescription] = useState(storyQuestion?.goal || "");
  const [openAIPromptModal, setOpenAIPromptModal] = useState(false);
  const isEditing = !!storyQuestion;

  useEffect(() => { if (open) setOpenAIPromptModal(false); }, [open]);

  useEffect(() => {
    if (!storyQuestion) return;

    setPromptDescription(storyQuestion.goal as string);
  }, [storyQuestion]);

  const restartState = useCallback(() => {
    onClosePromptGoalModal();
    setOpenAIPromptModal(false);
    setChanged(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyQuestion?.goal]);

  const handleChange = useCallback((value: any) => {
    if (isEditing) return;

    setPromptDescription(value);
    setChanged(true);
  }, [isEditing]);

  const onConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the prompt modal?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) restartState();
    },
    [changed, restartState]
  );

  const callbackHandler = (callback: any) => (storyQuestion: StoryQuestion) => {
    callback(storyQuestion);
    restartState();
  };

  const handleOpenAIPromptModal = () => {
    setOpenAIPromptModal(true);
    onClosePromptGoalModal();
  };

  return (
    <>
      <AIStoryQuestionModalForm
        storyQuestion={storyQuestion}
        initialPromptDescription={promptDescription}
        openModal={openAIPromptModal}
        onRestart={restartState}
        onCreate={callbackHandler(onCreate)}
        onUpdate={callbackHandler(onUpdate)}
      />
      {children(isEditing ? setOpenAIPromptModal : onOpenPromptGoalModal)}
      {open && (
        <ChqModalDialog className="AIPromptModal" onClose={onConfirmClose}>
          <ModalDialog.Heading onClose={onConfirmClose}>
            <Trans>Create new AI Story Prompt</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <Form onSubmit={() => {}}>
              <StringField autoFocus name="promptDescription" required onChange={handleChange}>
                <Trans>What are you looking to do?</Trans>
              </StringField>
              <div className={styles.form__actions}>
                <SubmitButton
                  primary
                  onClick={() => { if (promptDescription !== "") handleOpenAIPromptModal(); }}
                >
                  {submitting => (
                    submitting
                      ? <> <MagicWandIcon /> <Trans>Submitting...</Trans></>
                      : <> <MagicWandIcon /> <Trans>Build prompt page</Trans></>
                  )}
                </SubmitButton>
              </div>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default NewAIStoryQuestionModal;
